"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import type { PropsWithChildren, Usable } from "react";
import React, { Children, isValidElement, useState } from "react";
import type { ResolvedIntlConfig } from "react-intl";
import { Button, Carousel, CarouselContent, CarouselNext, CarouselPrevious, type CarouselApi } from "@repo/components/atoms";
import { useGetIntl } from "@repo/i18n/client";
import { cn } from "@repo/lib";
type CarouselOptions = React.ComponentProps<typeof Carousel>["opts"];
interface MediaChildProps {
  "data-thumbnail-enabled": boolean;
  "data-thumbnail-id": string;
  "data-thumbnail-src": string;
  "data-thumbnail-blur-data-url": string;
}
export function MediaViewer(t0) {
  const $ = _c(41);
  const {
    children,
    className,
    dictionaryPromise,
    lang,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start",
      dragFree: true,
      loop: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const opts = t1 satisfies CarouselOptions;
  const [api, setApi] = useState();
  let t2;
  if ($[1] !== dictionaryPromise || $[2] !== lang) {
    t2 = {
      dictionaryPromise,
      lang
    };
    $[1] = dictionaryPromise;
    $[2] = lang;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const intl = useGetIntl(t2);
  let t3;
  let t4;
  let t5;
  if ($[4] !== api || $[5] !== children || $[6] !== className || $[7] !== intl || $[8] !== title) {
    const arrayChildren = Children.toArray(children);
    const slides = arrayChildren.filter(_temp);
    const thumbnails = slides.filter(_temp2);
    let t6;
    if ($[12] !== className) {
      t6 = cn("landscape:h-dvh [&_>_div]:h-full grid", className);
      $[12] = className;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== slides) {
      t7 = <CarouselContent className="ms-0 h-full cursor-grab">{slides}</CarouselContent>;
      $[14] = slides;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== intl || $[17] !== title) {
      t8 = intl.formatMessage({
        defaultMessage: "Previous {title} image",
        description: "Button to show previous image in carousel",
        id: "LL1Pcc"
      }, {
        title
      });
      $[16] = intl;
      $[17] = title;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== t8) {
      t9 = <CarouselPrevious className="lg:hidden start-4 self-center" title={t8} />;
      $[19] = t8;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== intl || $[22] !== title) {
      t10 = intl.formatMessage({
        defaultMessage: "Next {title} image",
        description: "Button to show next image in carousel",
        id: "WlfIG6"
      }, {
        title
      });
      $[21] = intl;
      $[22] = title;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    let t11;
    if ($[24] !== t10) {
      t11 = <CarouselNext className="lg:hidden end-4 self-center" title={t10} />;
      $[24] = t10;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    if ($[26] !== t11 || $[27] !== t6 || $[28] !== t7 || $[29] !== t9) {
      t5 = <Carousel className={t6} opts={opts} setApi={setApi}>{t7}{t9}{t11}</Carousel>;
      $[26] = t11;
      $[27] = t6;
      $[28] = t7;
      $[29] = t9;
      $[30] = t5;
    } else {
      t5 = $[30];
    }
    t3 = "pointer-events-none absolute px-8 top-0 z-30 hidden h-full grid-flow-row content-center gap-4 lg:grid backdrop-blur-xs";
    let t12;
    if ($[31] !== api || $[32] !== intl || $[33] !== title) {
      t12 = (thumbnail, index) => <Button aria-label={intl.formatMessage({
        defaultMessage: "Show {title} image",
        description: "Button to show image in carousel",
        id: "dOx5bu"
      }, {
        index: index + 1,
        title
      })} className="pointer-events-auto w-20 relative my-auto aspect-square h-auto overflow-hidden rounded-full bg-background/50 shadow-md border border-background/50 cursor-crosshair *:scale-200 hover:*:scale-180 focus:*:scale-180 *:transition-transform *:duration-1000 *:ease-in-out" key={thumbnail.props["data-thumbnail-id"]} onClick={() => {
        api?.scrollTo(index);
      }} title={intl.formatMessage({
        defaultMessage: "Show {title} image",
        description: "Button to show image in carousel",
        id: "dOx5bu"
      }, {
        index: index + 1,
        title
      })}><Image alt={intl.formatMessage({
          defaultMessage: "Show {title} image",
          description: "Button to show image in carousel",
          id: "dOx5bu"
        }, {
          index: index + 1,
          title
        })} blurDataURL={thumbnail.props["data-thumbnail-blur-data-url"]} fill={true} sizes="5vw" src={thumbnail.props["data-thumbnail-src"]} style={{
          objectFit: "cover"
        }} /></Button>;
      $[31] = api;
      $[32] = intl;
      $[33] = title;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    t4 = thumbnails.map(t12);
    $[4] = api;
    $[5] = children;
    $[6] = className;
    $[7] = intl;
    $[8] = title;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[35] !== t3 || $[36] !== t4) {
    t6 = <nav className={t3}>{t4}</nav>;
    $[35] = t3;
    $[36] = t4;
    $[37] = t6;
  } else {
    t6 = $[37];
  }
  let t7;
  if ($[38] !== t5 || $[39] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
  } else {
    t7 = $[40];
  }
  return t7;
}
function _temp2(slide) {
  return slide.props["data-thumbnail-enabled"] === true;
}
function _temp(child) {
  return isValidElement(child);
}